import {
  Text,
  Card,
  Badge,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { PersonFeedbackRegular } from '@fluentui/react-icons';
import { HardwareRequestFormData } from '../../../hooks/useHardwareRequestForm';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXL,
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  card: {
    padding: tokens.spacingVerticalL,
    maxWidth: '700px',
  },
  infoRow: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
    marginBottom: tokens.spacingVerticalS,
  },
  label: {
    fontWeight: tokens.fontWeightSemibold,
    minWidth: '150px',
  },
  itemsList: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalXS,
  },
  justificationBox: {
    backgroundColor: tokens.colorNeutralBackground3,
    padding: tokens.spacingVerticalM,
    borderRadius: tokens.borderRadiusMedium,
    marginTop: tokens.spacingVerticalS,
  },
  notificationBox: {
    backgroundColor: tokens.colorBrandBackground2,
    padding: tokens.spacingVerticalM,
    borderRadius: tokens.borderRadiusMedium,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  icon: {
    marginRight: tokens.spacingHorizontalXS,
    verticalAlign: 'middle',
  },
});

interface ReviewStepProps {
  formData: HardwareRequestFormData;
}

export function ReviewStep({ formData }: ReviewStepProps) {
  const styles = useStyles();

  return (
    <div className={styles.container}>
      <Text size={500} weight="semibold">
        Review Hardware Request
      </Text>
      <Text>Please review your hardware request before submitting.</Text>

      {/* Employee Details */}
      <div className={styles.section}>
        <Text size={400} weight="semibold">
          Employee Details
        </Text>
        <Card className={styles.card}>
          <div className={styles.infoRow}>
            <Text className={styles.label}>Employee:</Text>
            <Text>{formData.selectedUserName}</Text>
          </div>
          <div className={styles.infoRow}>
            <Text className={styles.label}>Email:</Text>
            <Text>{formData.selectedUserEmail}</Text>
          </div>
          {formData.managerName && (
            <div className={styles.infoRow}>
              <Text className={styles.label}>Line Manager:</Text>
              <Text>{formData.managerName}</Text>
            </div>
          )}
        </Card>
      </div>

      {/* Hardware Summary */}
      <div className={styles.section}>
        <Text size={400} weight="semibold">
          Requested Hardware
        </Text>
        <Card className={styles.card}>
          {formData.mainHardware.length > 0 && (
            <div style={{ marginBottom: tokens.spacingVerticalM }}>
              <Text className={styles.label}>Main Hardware:</Text>
              <div className={styles.itemsList}>
                {formData.mainHardware.map((item) => (
                  <Badge key={item} appearance="filled" color="brand">
                    {item}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {formData.peripherals.length > 0 && (
            <div style={{ marginBottom: tokens.spacingVerticalM }}>
              <Text className={styles.label}>Peripherals:</Text>
              <div className={styles.itemsList}>
                {formData.peripherals.map((item) => (
                  <Badge key={item} appearance="filled" color="informative">
                    {item}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          <div className={styles.infoRow}>
            <Text className={styles.label}>Monitors:</Text>
            <Text>
              {formData.monitorQuantity === 0
                ? 'No monitors'
                : `${formData.monitorQuantity} monitor${formData.monitorQuantity > 1 ? 's' : ''}`}
            </Text>
          </div>
        </Card>
      </div>

      {/* Justification */}
      <div className={styles.section}>
        <Text size={400} weight="semibold">
          Business Justification
        </Text>
        <Card className={styles.card}>
          <div className={styles.justificationBox}>
            <Text>{formData.justification}</Text>
          </div>
        </Card>
      </div>

      {/* Notification Info */}
      <div className={styles.section}>
        <Text size={400} weight="semibold">
          Notifications
        </Text>
        <div className={styles.notificationBox}>
          <Text>
            <PersonFeedbackRegular className={styles.icon} />
            Upon submission, the following will be notified:
          </Text>
          <div style={{ marginLeft: tokens.spacingHorizontalXL }}>
            {formData.managerEmail && (
              <Text>
                • Line Manager ({formData.managerEmail})
              </Text>
            )}
            <Text>• IT Administrators</Text>
          </div>
        </div>
      </div>
    </div>
  );
}
